/**
 * What the help chat tells the engineers about where the customer is: the
 * screen, the visit on it and the last error shown. A plain module-level
 * record the screens write to; nothing renders from it, so it is not React
 * state. The server treats it as display-only (SUPPORT_CHAT_SPEC.md).
 */
import { ApiError } from "../api";
import type { VendSession } from "../vend";
import type { ClientContext } from "./supportApi";

export const CONTEXT_FIELD_MAX = 200;

export interface SupportSnapshot {
  route: string | null;
  visit: { vend_session_id: string; state: string; outcome_reason: string | null } | null;
  lastError: { key: string; code: string | null; traceId: string | null } | null;
}

let snapshot: SupportSnapshot = { route: null, visit: null, lastError: null };

export function getSupportSnapshot(): SupportSnapshot {
  return snapshot;
}

export function reportRoute(route: string): void {
  snapshot = { ...snapshot, route };
}

/** The visit on screen. It stays the "last visit" after leaving the screen. */
export function reportVisit(v: {
  vend_session_id: string;
  state: VendSession["state"] | string;
  outcome_reason?: string | null | undefined;
}): void {
  snapshot = {
    ...snapshot,
    visit: { vend_session_id: v.vend_session_id, state: v.state, outcome_reason: v.outcome_reason ?? null },
  };
}

/** An error the customer was shown: its catalogue key, plus code/trace when the API said so. */
export function reportError(key: string, e?: unknown): void {
  const api = e instanceof ApiError ? e : null;
  snapshot = {
    ...snapshot,
    lastError: { key, code: api?.code ?? null, traceId: api?.traceId ?? null },
  };
}

/** Reset (tests). */
export function resetSupportSnapshot(): void {
  snapshot = { route: null, visit: null, lastError: null };
}

/** At most `max` characters (code points, as the server counts them). */
export function clip(value: string, max = CONTEXT_FIELD_MAX): string {
  const chars = Array.from(value);
  return chars.length <= max ? value : chars.slice(0, max - 1).join("") + "…";
}

export interface BuildEnv {
  appBuild: string;
  userAgent: string;
}

function defaultEnv(): BuildEnv {
  return {
    appBuild: typeof __APP_BUILD__ === "string" ? __APP_BUILD__ : "dev",
    userAgent: globalThis.navigator?.userAgent ?? "",
  };
}

export function buildClientContext(
  from: SupportSnapshot = snapshot,
  env: BuildEnv = defaultEnv(),
): ClientContext {
  const ctx: ClientContext = {};
  const put = (field: keyof ClientContext, value: string | null | undefined) => {
    if (value) ctx[field] = clip(value);
  };
  put("route", from.route);
  put("vend_session_id", from.visit?.vend_session_id);
  put("vend_state", from.visit?.state);
  put("outcome_reason", from.visit?.outcome_reason);
  // The API's code is more precise than the catalogue key; the key covers
  // errors that never reached the API (a bad QR code, the network).
  put("last_error_code", from.lastError ? (from.lastError.code ?? from.lastError.key) : null);
  put("last_trace_id", from.lastError?.traceId);
  put("app_build", env.appBuild);
  put("user_agent", env.userAgent);
  return ctx;
}
